/**
 * WHERE A PAGE SCROLLS, STATED BY THE PAGE ITSELF.
 *
 * D-096 made the route element the page's scroll container and closed two costs by having the
 * shell settle that element after every navigation: start it at the top, and give it focus so
 * PageDown and Space reach the content without a click first. Issue #371 then moved the scroller
 * inward on every TABBED page - "one element per page, at the tab body or its content wrapper, is
 * `overflow: auto`" - so the header, the banners and the tab strip stay put while only the active
 * tab's body scrolls. Both of the shell's services kept pointing at the route element, which on
 * those pages no longer scrolls: the keyboard moved nothing, and a tab change landed wherever the
 * previous tab had been left. See D-131.
 *
 * WHY THE PAGE DECLARES IT RATHER THAN THE SHELL FINDING IT. A page saying where it scrolls is
 * stating a fact about itself, and a fact does not drift. The alternatives both do: a shell that
 * searched the subtree for the single `overflow: auto` element would be guessing, and every page
 * re-implementing "what happens on arrival" would be ten copies of a rule the shell owns, drifting
 * the moment somebody writes an eleventh page.
 *
 * READ DEFENSIVELY, AND THAT IS THE POINT. A page that declares nothing - every untabbed route,
 * and the certificate CREATE pages, which compose the detail pages' sheet but render no tab group
 * and so still scroll at their host - gets exactly the behaviour it has today. So does a page whose
 * declaration returns null because its tab group has not rendered yet. This cannot regress a page
 * that does not opt in.
 */

/**
 * Implemented by a route page whose content scrolls somewhere other than its own host element.
 * The member is a getter rather than a stored field because the element is inside a shadow root
 * that is rebuilt as the page re-renders, so the answer has to be read at the moment it is asked.
 */
export interface PageScrollElementHost {
  /** The element this page scrolls, or null while it does not yet exist. */
  readonly pageScrollElement: HTMLElement | null;
}

/**
 * The element to settle for `route`: the one it declares, or the route element itself.
 *
 * Structural rather than an `instanceof` check on a class, because route elements share no base -
 * they extend `LitElement` directly - and a page should be able to state this fact without
 * inheriting anything to do it.
 */
export function pageScrollElementOf(route: HTMLElement): HTMLElement {
  const declared = (route as Partial<PageScrollElementHost>).pageScrollElement;
  return declared instanceof HTMLElement ? declared : route;
}

/**
 * The scrolling element of a page built on a single `sl-tab-group`, which is every tabbed page
 * here: Shoelace's tab group scrolls its `body` part, and the six stylesheets that set
 * `overflow: auto` all set it there.
 *
 * THE ONE PLACE THAT KNOWS THIS. Reaching through a third party's shadow root is a liability, so
 * it is spent once here instead of in each page. If Shoelace renames the part, this returns null,
 * every page falls back to its host, and the failure is the behaviour we had before this - not a
 * crash and not a wrong element.
 */
export function tabGroupScrollElement(host: HTMLElement): HTMLElement | null {
  const group = host.shadowRoot?.querySelector('sl-tab-group');
  const body = group?.shadowRoot?.querySelector('[part~="body"]');
  return body instanceof HTMLElement ? body : null;
}
